import { Text } from "@/src/components/ui/BrandText";
import { text as uiText, surface, border, LEGACY_CURRENT } from "@/src/theme/tokens";
import { useNotifications } from "@/src/hooks/useNotifications";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { Ionicons } from "@/src/components/ui/ionicons";
import { router } from "expo-router";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import React from "react";
import { FlatList, Pressable, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { auth, db } from "../../src/lib/firebase";

const BG = LEGACY_CURRENT.background;
const ACCENT = LEGACY_CURRENT.accent;
const CARD = LEGACY_CURRENT.card;
const BORDER = LEGACY_CURRENT.border;
const MUTED = uiText.muted;

function formatWhen(createdAtMs: number) {
  if (!createdAtMs) return "";
  const date = new Date(createdAtMs);
  const now = new Date();
  const diff = now.getTime() - createdAtMs;
  if (diff < 60 * 1000) return "A l'instant";
  if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (60 * 1000)))} min`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (60 * 60 * 1000)))} h`;
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString([], sameYear ? { day: "2-digit", month: "2-digit" } : { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function NotificationsScreen() {
  const { items, count, unreadCount, loading, error: notificationsError, retry } = useNotifications();
  const { setActiveAccount } = useActiveAccount();

  const notificationErrorView = notificationsError ? (
    <View style={{ backgroundColor: surface.danger, borderWidth: 1, borderColor: border.danger, borderRadius: 14, padding: 12, marginTop: 10, gap: 8 }}>
      <Text style={{ color: uiText.danger, fontWeight: "800" }}>{notificationsError.message}</Text>
      <Pressable onPress={retry} disabled={loading} accessibilityRole="button" accessibilityLabel="Réessayer de charger les notifications" style={{ alignSelf: "flex-start", minHeight: 44, justifyContent: "center", paddingHorizontal: 10, opacity: loading ? 0.6 : 1 }}>
        <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
      </Pressable>
    </View>
  ) : null;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "left", "right"]}>
      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 10, paddingBottom: 24, gap: 10 }}
        ListHeaderComponent={
          <View style={{ marginBottom: 12 }}>
            <Text style={{ color: uiText.primary, fontSize: 28, fontWeight: "900" }}>Notifications</Text>
            <Text style={{ color: MUTED, marginTop: 4 }}>
              {loading
                ? "Chargement..."
                : notificationsError && count === 0
                  ? "Notifications indisponibles"
                  : `${count} notification${count > 1 ? "s" : ""}${unreadCount > 0 ? ` - ${unreadCount} non lue${unreadCount > 1 ? "s" : ""}` : ""}`}
            </Text>
            {notificationsError && items.length > 0 ? notificationErrorView : null}
          </View>
        }
        ListEmptyComponent={loading ? null : notificationsError ? notificationErrorView : (
          <View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 18, padding: 18, alignItems: "center", gap: 8 }}>
            <Ionicons name="notifications-outline" size={28} color={uiText.muted} />
            <Text style={{ color: uiText.primary, fontWeight: "800" }}>Aucune notification</Text>
            <Text style={{ color: MUTED, textAlign: "center" }}>Les nouveaux messages et commentaires sur tes posts apparaitront ici.</Text>
          </View>
        )}
        renderItem={({ item }) => {
          const iconName = item.type === "message" || item.type === "direct_message" ? "chatbubble-ellipses-outline" : "chatbox-ellipses-outline";
          return (
            <Pressable
              onPress={async () => {
                const uid = auth.currentUser?.uid;
                if (uid) {
                  try {
                    await updateDoc(doc(db, "users", uid, "notifications", item.id), { readAt: serverTimestamp(), openedAt: serverTimestamp() });
                  } catch {}
                }
                if (item.route.pathname === "/(tabs)/messages/direct/[id]") {
                  const selected = await setActiveAccount(item.route.params.identityId);
                  if (!selected) {
                    router.replace("/(tabs)/messages");
                    return;
                  }
                }
                router.push(item.route as any);
              }}
              style={{ backgroundColor: CARD, borderWidth: 1, borderColor: item.read ? BORDER : surface.selected, borderRadius: 18, padding: 14, flexDirection: "row", gap: 12, alignItems: "flex-start" }}
            >
              <View style={{ width: 42, height: 42, borderRadius: 21, alignItems: "center", justifyContent: "center", backgroundColor: surface.selected, borderWidth: 1, borderColor: border.brand }}>
                <Ionicons name={item.type === "follow" ? "person-add-outline" : iconName} size={18} color={uiText.brand} />
              </View>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <Text style={{ color: uiText.primary, fontWeight: item.read ? "800" : "900", flex: 1 }}>{item.title}</Text>
                  <Text style={{ color: MUTED, fontSize: 12, fontWeight: "700" }}>{formatWhen(item.createdAtMs)}</Text>
                </View>
                <Text style={{ color: uiText.primary, marginTop: 6, lineHeight: 20 }}>{item.body}</Text>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 6, marginTop: 10 }}>
                  {!item.read ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: ACCENT }} /> : null}
                  <Text style={{ color: ACCENT, fontWeight: "800" }}>Ouvrir</Text>
                  <Ionicons name="chevron-forward" size={14} color={ACCENT} />
                </View>
              </View>
            </Pressable>
          );
        }}
      />
    </SafeAreaView>
  );
}
